import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import Papa from 'papaparse';
import { Store } from '@ngrx/store';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { Subject, takeUntil, timeout } from 'rxjs';
import { SourcedItem, SourcingOutcomeInformation, VendorsList } from 'src/app/shared/models/app-models/SourcingOutcome.model';
import { isEmpty } from 'lodash';

enum StatusMessageType {
  success = 1,
  info,
  error,
}

export interface StatusMessage {
  message: string | null;
  type: StatusMessageType;
}

@Component({
  selector: 'app-excel-upload-diolog',
  templateUrl: './excel-upload-diolog.component.html',
  styleUrls: ['./excel-upload-diolog.component.scss']
})
export class ExcelUpdateDiologComponent {
  destroy$: Subject<boolean> = new Subject<boolean>();
  readonly dialogRef = inject(MatDialogRef<ExcelUpdateDiologComponent>);
  selection: SourcedItem[] = [];
  sourcedItem: SourcedItem[] = [];
  fileName: string | null = null;
  loading: boolean = false;
  file: File | null = null;

  statusMessage: StatusMessage[] =  [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
  ) {
    this.iconRegistry.addSvgIcon(
      'mp-cloud-update',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/material-icons/material-icon-cloud-update.svg')
    );
    this.iconRegistry.addSvgIcon(
      'mp-description',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/material-icons/material-icon-description.svg')
    );
    this.iconRegistry.addSvgIcon(
      'mp-straighten',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/material-icons/material-icon-straighten.svg')
    );

    this.sourcedItem = data.sourcedItem ?? [];
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  handleClose() {
    this.dialogRef.close();
  }

  handleSubmit() {
    this.dialogRef.close({
      confirm: true,
      sourcedItem: this.selection
    });
  }

  dropped(files: NgxFileDropEntry[]) {
    this.statusMessage = [
      {
        message: `Initialising upload, please wait and do not close this window`,
        type: StatusMessageType.info
      },
    ];

    // simulate upload...?, but why
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.processFile(file);
        });
      }
    }
  }

  processFile(file: File) {
    if (file && file.name) {
      this.file = file;
      // Invalid file type. Please check and try again.
      if(!(/^.+\.csv$/.test(file.name))) {
        this.statusMessage = [
          {
            message: `Invalid file type. Please check and try again.`,
            type: StatusMessageType.error
          },
        ];

        return;
      }

      // size is more than 50mb
      if(file.size > (50 * 1024 * 1024)) {
        this.statusMessage = [
          {
            message: `File size exceeded the maximum limit of 50mb.`,
            type: StatusMessageType.error
          },
        ];

        return;
      }

      this.selection = [];
      Papa.parse(file, {
        header: true,
        complete: (result) => {
          let importedList: SourcedItem[] = result.data as SourcedItem[];
          // filter out parse issue with blank data
          importedList = importedList.filter((item) => !isEmpty(item.PurchaseItemNumber));

          // No record was found in the file. Please upload at least 1 record.
          if(importedList.length == 0) {
            this.statusMessage = [
              {
                message: `No record was found in the file. Please upload at least 1 record.`,
                type: StatusMessageType.error
              },
            ];
    
            return;
          }

          // mismatch
          if(importedList.filter((item) => !this.sourcedItem.some((si) => si.PurchaseItemId && si.PurchaseItemId == item.PurchaseItemId)).length > 0) {
            this.statusMessage = [
              {
                message: `Upload failed: <b>${file.name}</b>`,
                type: StatusMessageType.error
              },
              {
                message: `This file contains mismatched data from the Items table.`,
                type: StatusMessageType.error
              },
              {
                message: `Please double-check the file or download the Excel file again.`,
                type: StatusMessageType.error
              },
            ];

            return;
          }
          
          this.selection = this.sourcedItem.filter((item) => importedList.some((il) => il.PurchaseItemId == item.PurchaseItemId	));

          this.statusMessage = [
            {
              message: `Upload successful: <b>${file.name}</b>`,
              type: StatusMessageType.success
            },
            {
              message: `<b>${this.selection.length} Items</b> will be checked/selected.`,
              type: StatusMessageType.success
            },
            {
              message: `Please proceed by clicking the "Upload" button.`,
              type: StatusMessageType.success
            },
          ];
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
        },
      });
    }
  }

  removeFile() {
    this.file = null;
    this.statusMessage = [];
  }

  hasMessageError(): boolean {
    return this.statusMessage.some((item) => item.type === StatusMessageType.error)
  }

}
